import { useMemo, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Cable, Pencil, Plus, Search, Server, Trash2 } from 'lucide-react';
import type { Olt, PonPort, Splitter, Topology, Zone } from '@simpleacs/shared';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { useTasks } from '../lib/tasks';
import { ConfirmDeleteDialog } from './OpticalSplitterDialog';
import { ZoneSelect } from '../components/topology-select';

/**
 * The ODF registry, flat on purpose: every frame in one table without the
 * topology UI — which OLT port feeds it, which zone it serves, how many
 * splitter devices live inside. The same rows the topology tree manages;
 * creating one here is creating it there.
 */
export function OdfPage() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ existing: Splitter | null } | null>(null);
  const [deleting, setDeleting] = useState<Splitter | null>(null);
  const [filter, setFilter] = useState('');

  const topologyQuery = useQuery({
    queryKey: ['topology'],
    queryFn: () => api.get<Topology>('/topology'),
  });
  const oltsQuery = useQuery({
    queryKey: ['olts'],
    queryFn: () => api.get<{ items: Olt[] }>('/olts'),
  });
  const topology = topologyQuery.data;
  const olts = oltsQuery.data?.items ?? [];
  const oltNames = useMemo(() => new Map(olts.map((o) => [o.id, o.name])), [olts]);
  const zoneNames = useMemo(
    () => new Map((topology?.zones ?? []).map((z) => [z.id, z.name])),
    [topology],
  );

  const odfs = useMemo(
    () => (topology?.splitters ?? []).filter((s) => s.kind === 'odf'),
    [topology],
  );
  const housedCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const device of topology?.opticalSplitters ?? []) {
      if (device.housingId) counts.set(device.housingId, (counts.get(device.housingId) ?? 0) + 1);
    }
    return counts;
  }, [topology]);
  const childCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of topology?.splitters ?? []) {
      if (s.parentId) counts.set(s.parentId, (counts.get(s.parentId) ?? 0) + 1);
    }
    return counts;
  }, [topology]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const rows = q
      ? odfs.filter(
          (o) =>
            o.name.toLowerCase().includes(q) ||
            (o.oltId && (oltNames.get(o.oltId) ?? '').toLowerCase().includes(q)) ||
            (o.oltPonPort ?? '').toLowerCase().includes(q),
        )
      : odfs;
    return [...rows].sort((a, b) => a.name.localeCompare(b.name));
  }, [odfs, oltNames, filter]);

  const fed = odfs.filter((o) => o.oltPonPort).length;

  return (
    <>
      <PageHeader
        title="ODF"
        description="Every distribution frame — which OLT port feeds it, and what lives inside."
        actions={
          can('topology.manage') ? (
            <Button
              variant="primary"
              icon={<Plus className="size-4" />}
              onClick={() => setDialog({ existing: null })}
            >
              New ODF
            </Button>
          ) : null
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="ODFs" value={odfs.length} icon={<Server className="size-4" />} />
        <StatCard label="Fed from an OLT port" value={fed} />
        <StatCard
          label="No port recorded"
          value={odfs.length - fed}
          tone={odfs.length - fed > 0 ? 'warn' : 'default'}
        />
        <StatCard
          label="Splitters housed"
          value={odfs.reduce((sum, o) => sum + (housedCounts.get(o.id) ?? 0), 0)}
        />
      </div>

      {topologyQuery.isLoading ? (
        <div className="flex justify-center p-16">
          <Spinner className="size-6" />
        </div>
      ) : topologyQuery.error ? (
        <div className="mt-5">
          <ErrorNotice error={topologyQuery.error} />
        </div>
      ) : (
        <div className="mt-5">
          <Card
            title="Frames"
            action={
              <span className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-faint" />
                <input
                  className="input h-8 w-56 pl-8 text-xs"
                  placeholder="Filter by name, OLT, port…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </span>
            }
          >
            {shown.length === 0 ? (
              <EmptyState
                icon={<Server className="size-8" />}
                title={odfs.length === 0 ? 'No ODFs yet' : 'Nothing matches'}
                description={
                  odfs.length === 0
                    ? 'Create the frame each PON port lands on — here or from the topology tree.'
                    : 'No frame matches that filter.'
                }
              />
            ) : (
              <Table>
                <THead>
                  <th className="th">Name</th>
                  <th className="th">Fed from</th>
                  <th className="th">Zone</th>
                  <th className="th">Splitters inside</th>
                  <th className="th">Sub-stages</th>
                  <th className="th">ONUs</th>
                  {can('topology.manage') && <th className="th w-20" />}
                </THead>
                <TBody>
                  {shown.map((odf) => (
                    <TRow key={odf.id}>
                      <td className="td font-medium text-ink">{odf.name}</td>
                      <td className="td">
                        {odf.oltPonPort ? (
                          <span className="flex items-center gap-1.5 text-muted">
                            <Cable className="size-3.5 text-faint" />
                            {odf.oltId ? (oltNames.get(odf.oltId) ?? 'OLT') : 'OLT'} ·{' '}
                            <span className="font-mono text-xs">{odf.oltPonPort}</span>
                          </span>
                        ) : (
                          <Pill tone="warn">no port</Pill>
                        )}
                      </td>
                      <td className="td text-muted">
                        {odf.zoneId ? (zoneNames.get(odf.zoneId) ?? '—') : '—'}
                      </td>
                      <td className="td">{housedCounts.get(odf.id) ?? 0}</td>
                      <td className="td">{childCounts.get(odf.id) ?? 0}</td>
                      <td className="td">{odf.deviceCount ?? 0}</td>
                      {can('topology.manage') && (
                        <td className="td">
                          <span className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Edit ODF"
                              icon={<Pencil className="size-3.5" />}
                              onClick={() => setDialog({ existing: odf })}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Delete ODF"
                              icon={<Trash2 className="size-3.5" />}
                              onClick={() => setDeleting(odf)}
                            />
                          </span>
                        </td>
                      )}
                    </TRow>
                  ))}
                </TBody>
              </Table>
            )}
          </Card>
        </div>
      )}

      {dialog && topology && (
        <OdfDialog
          existing={dialog.existing}
          zones={topology.zones}
          olts={olts}
          onClose={() => setDialog(null)}
        />
      )}
      {deleting && (
        <ConfirmDeleteDialog
          title={`Delete ${deleting.name}?`}
          body={
            <p>
              The frame is removed from the registry — nothing on the chassis changes. A frame
              with sub-stages, ONUs, or splitters housed inside is refused by name.
            </p>
          }
          onConfirm={async () => {
            await api.delete(`/splitters/${encodeURIComponent(deleting.id)}`);
            await queryClient.invalidateQueries({ queryKey: ['topology'] });
          }}
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
}

/**
 * Create/edit a frame. Same endpoint the topology tree uses (kind stays odf),
 * with the fields a frame actually has: name, ports, zone, and the OLT PON
 * port that feeds it. Submit closes the dialog at once and the write runs in
 * the tasks tray; the topology refreshes when it lands.
 */
function OdfDialog({
  existing,
  zones,
  olts,
  onClose,
}: {
  existing: Splitter | null;
  zones: Zone[];
  olts: Olt[];
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [form, setForm] = useState({
    name: existing?.name ?? '',
    capacity: String(existing?.capacity ?? 8),
    zoneId: existing?.zoneId ?? '',
    oltId: existing?.oltId ?? '',
    oltPonPort: existing?.oltPonPort ?? '',
  });

  const portsQuery = useQuery({
    queryKey: ['olt-pon-ports', form.oltId],
    queryFn: () =>
      api.get<{ items: PonPort[] }>(`/olts/${encodeURIComponent(form.oltId)}/pon-ports`),
    enabled: form.oltId !== '',
  });
  const ports = portsQuery.data?.items ?? [];

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    // Snapshotted: the dialog is gone by the time the write settles.
    const body = {
      name: form.name.trim(),
      kind: 'odf' as const,
      capacity: Number(form.capacity) || 8,
      zoneId: form.zoneId || null,
      oltId: form.oltId || null,
      oltPonPort: form.oltPonPort || null,
    };
    onClose();
    tasks.start({
      label: existing ? `Save ODF ${existing.name}` : `Create ODF ${body.name}`,
      run: () =>
        existing
          ? api.patch(`/splitters/${encodeURIComponent(existing.id)}`, body)
          : api.post('/splitters', body),
      done: () => `${body.name} ${existing ? 'saved' : 'created'}.`,
      invalidate: [['topology']],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <form
        onSubmit={onSubmit}
        className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl"
      >
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">
            {existing ? `Edit ${existing.name}` : 'New ODF'}
          </h2>
        </header>
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <Field label="Name">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="ODF-POP-01"
              required
              autoFocus
            />
          </Field>
          <Field label="Ports">
            <Input
              type="number"
              min={1}
              value={form.capacity}
              onChange={(e) => setForm({ ...form, capacity: e.target.value })}
            />
          </Field>
          <Field label="Zone">
            <ZoneSelect
              value={form.zoneId}
              zones={zones}
              emptyLabel="No zone"
              onChange={(next) => setForm({ ...form, zoneId: next })}
            />
          </Field>
          <Field label="OLT">
            <select
              className="input"
              value={form.oltId}
              onChange={(e) => setForm({ ...form, oltId: e.target.value, oltPonPort: '' })}
            >
              <option value="">Not attached</option>
              {olts.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </Field>
          {form.oltId && (
            <Field label="PON port">
              <select
                className="input"
                value={form.oltPonPort}
                onChange={(e) => setForm({ ...form, oltPonPort: e.target.value })}
              >
                <option value="">Choose a port…</option>
                {ports.map((p) => (
                  <option key={p.id} value={p.port}>
                    {p.port} · {p.onuCount} ONUs
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3.5">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            {existing ? 'Save' : 'Create'}
          </Button>
        </footer>
      </form>
    </div>
  );
}
